import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import cn from "classnames";
import { Trash2, GripVertical } from "lucide-react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./Collabs.module.sass";
import { getAuthToken } from "../../utils/authHelpers";
import Icon from "../../components/Icon";
import Dropdown from "../../components/Dropdown";
import ExploreNewCollabModal from "./ExploreNewCollabModal";
import { displayHandle } from "../../utils/streamHelpers";
import BlurredGate from "../../components/BlurredGate";
import CollabIntroVideo from "../../components/CollabIntroVideo";
import ApplicantCard from "../../components/ApplicantCard";
import CollabPersonaModal from "../../components/CollabPersonaModal";
import { usePersona } from "../../contexts/PersonaContext";

const CARD_STEPS = [
    { id: 'agreements', label: 'Expectations', num: 1 },
    { id: 'media',      label: 'Content',      num: 2 },
    { id: 'review',     label: 'Review',        num: 3 },
];

// Collapsible wrapper for each Inbox section so the host can declutter the
// page and focus on one group of applications at a time.
const InboxSection = ({ title, count, subtitle, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className={cn(styles.requestSection, styles.accordionSection, { [styles.accordionOpen]: open })}>
            <button
                type="button"
                className={styles.accordionHeader}
                onClick={() => setOpen(prev => !prev)}
                aria-expanded={open}
            >
                <div className={styles.accordionHeaderText}>
                    <h3 className={styles.sectionTitle}>
                        {title} <span className={styles.accordionCount}>{count}</span>
                    </h3>
                    {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
                </div>
                <span className={styles.accordionChevron} aria-hidden>
                    <Icon name="arrow-bottom" size="18" />
                </span>
            </button>
            {open && <div className={styles.accordionBody}>{children}</div>}
        </div>
    );
};

const PropertyChip = ({ property }) => {
    if (!property) return null;
    const label = property.slug ? `@${property.slug}` : property.name || property.location || 'Property';
    return (
        <div className={styles.propertyChip}>
            {property.imageUrl ? (
                <img src={property.imageUrl} alt={label} className={styles.propertyChipImg} />
            ) : (
                <div className={styles.propertyChipIcon}>
                    <Icon name="home" size="12" />
                </div>
            )}
            <span className={styles.propertyChipLabel}>{label}</span>
        </div>
    );
};

const CollabCard = ({ collab, onClick, onRemove, isAdmin = false, onAdminComplete, adminBusy = false, onNudge, nudgeState = null, dragHandle = null, onRoleSwitched = null }) => {
    const partner = collab.partners?.[0];
    const snapshot = collab.snapshot || {};

    // Merged host+creator accounts can flip which side of the collab they're
    // on (e.g. accidentally recorded as the creator when they meant to collab
    // as their property's host) without recreating the collab.
    const { roles: personaRoles } = usePersona();
    const isDualPersona = personaRoles.includes('creator') && personaRoles.includes('host');
    const [switchingRole, setSwitchingRole] = useState(false);
    const myRole = collab.myRole;
    const otherRole = myRole === 'host' ? 'creator' : 'host';
    const canSwitchRole =
        !isAdmin &&
        typeof onRoleSwitched === 'function' &&
        isDualPersona &&
        (myRole === 'creator' || myRole === 'host') &&
        collab.status === 'active';

    const handleSwitchRole = async (e) => {
        e.stopPropagation();
        if (switchingRole) return;
        const token = getAuthToken();
        if (!token) return;
        setSwitchingRole(true);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const res = await fetch(`${apiBase}/api/collab/switch-role`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ collaborationId: collab.id, role: otherRole }),
            });
            const result = await res.json();
            if (result?.ok) {
                onRoleSwitched();
            } else {
                alert('Failed to switch role: ' + (result?.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error switching collab role:', err);
            alert('Failed to switch role');
        } finally {
            setSwitchingRole(false);
        }
    };

    // Counterpart nudge state. The server tells us what's outstanding and
    // whether we're still inside the cooldown; local state reflects an in-flight
    // or just-completed send so the UI updates without a refetch.
    const outstanding = collab.counterpartOutstanding || [];
    const primaryTask = outstanding[0] || null;
    const nudgeBusy = !!nudgeState?.busy;
    const nudgeError = nudgeState?.error || null;
    const alreadySent = nudgeState?.sent || collab.nudge?.canSend === false;
    const lastSentAt = nudgeState?.lastSentAt || collab.nudge?.lastSentAt || null;
    const partnerName = partner?.handle ? displayHandle(partner.handle) : (partner?.firstName || 'your collaborator');
    const canNudge =
        typeof onNudge === 'function' &&
        collab.status === 'active' &&
        !!primaryTask;
    
    const displayTitle = collab.title || snapshot.title || 
        (partner?.handle ? displayHandle(partner.handle) : partner?.firstName) || 
        'Collaboration';
    
    const statusColors = {
        active: { bg: '#ecfdf5', border: '#10b981', text: '#065f46' },
        paused: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
        completed: { bg: '#f0f9ff', border: '#0284c7', text: '#0369a1' },
        cancelled: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
        declined: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
    };
    
    const colors = statusColors[collab.status] || statusColors.active;
    const sp = collab.stepProgress;
    
    return (
        <div 
            className={styles.collabCard}
            onClick={onClick}
            style={{ borderLeftColor: colors.border }}
        >
            <div className={styles.cardHeader}>
                {dragHandle}
                {partner?.avatarUrl ? (
                    <img 
                        src={partner.avatarUrl} 
                        alt={partner.firstName || 'Partner'} 
                        className={styles.partnerAvatar}
                    />
                ) : (
                    <div className={styles.partnerAvatarPlaceholder}>
                        <Icon name="user" size="20" />
                    </div>
                )}
                <div className={styles.cardInfo}>
                    <h4 className={styles.cardTitle}>{displayTitle}</h4>
                    <p className={styles.cardPartner}>
                        with {partner?.handle ? displayHandle(partner.handle) : partner?.firstName || 'Partner'}
                    </p>
                </div>
                <span 
                    className={styles.statusBadge}
                    style={{ 
                        backgroundColor: colors.bg, 
                        color: colors.text,
                        borderColor: colors.border 
                    }}
                >
                    {collab.status}
                </span>
                {collab.completionSource === 'admin_override' && (
                    <span
                        className={styles.adminBadge}
                        title={collab.overrideReason || 'Completed by an admin override'}
                    >
                        Admin-completed
                    </span>
                )}
            </div>

            <PropertyChip property={collab.property} />

            {canSwitchRole && (
                <div className={styles.roleSwitchRow}>
                    <span>You&rsquo;re in this collab as a <strong>{myRole}</strong></span>
                    <button
                        type="button"
                        className={styles.roleSwitchBtn}
                        onClick={handleSwitchRole}
                        disabled={switchingRole}
                        title={`Switch your side of this collab to ${otherRole}`}
                    >
                        {switchingRole ? 'Switching…' : `Switch to ${otherRole}`}
                    </button>
                </div>
            )}

            {sp && collab.status === 'active' && (
                <div className={styles.progressSection}>
                    <div className={styles.progressHeader}>
                        <span className={styles.progressPercent}>{sp.percent}%</span>
                        <span className={styles.progressHint}>
                            {sp.percent === 100
                                ? 'All stages complete'
                                : `${sp.completedCount} of ${sp.totalCount} stages complete`}
                        </span>
                    </div>
                    <div className={styles.segmentedBar}>
                        {CARD_STEPS.map((step, idx) => {
                            const complete = sp.steps?.[step.id]?.complete;
                            const isCurrent = sp.nextStepId === step.id;
                            return (
                                <div
                                    key={step.id}
                                    className={cn(styles.barSegment, {
                                        [styles.segmentComplete]: complete,
                                        [styles.segmentCurrent]: isCurrent && !complete,
                                    })}
                                >
                                    <div className={styles.segmentFill} />
                                    {idx < CARD_STEPS.length - 1 && <span className={styles.segmentDivider} />}
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.segmentLabels}>
                        {CARD_STEPS.map((step) => {
                            const complete = sp.steps?.[step.id]?.complete;
                            const isCurrent = sp.nextStepId === step.id;
                            return (
                                <div
                                    key={step.id}
                                    className={cn(styles.segmentLabel, {
                                        [styles.labelComplete]: complete,
                                        [styles.labelCurrent]: isCurrent && !complete,
                                    })}
                                >
                                    <span className={styles.segmentMarker}>
                                        {complete ? '✓' : step.num}
                                    </span>
                                    <span className={styles.segmentName}>{step.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            
            {canNudge && !alreadySent && (
                <div className={styles.nudgeHint}>
                    Looks like {partnerName} {primaryTask.hint}.
                </div>
            )}

            <div className={styles.cardFooter}>
                <div className={styles.cardDates}>
                    {collab.startDate && collab.endDate ? (
                        <>
                            <span className={styles.cardDate}>
                                <strong>Start:</strong> {new Date(collab.startDate + 'T00:00:00').toLocaleDateString()}
                            </span>
                            <span className={styles.cardDateSep}>–</span>
                            <span className={styles.cardDate}>
                                <strong>End:</strong> {new Date(collab.endDate + 'T00:00:00').toLocaleDateString()}
                            </span>
                        </>
                    ) : collab.startDate ? (
                        <span className={styles.cardDate}>
                            <strong>Start:</strong> {new Date(collab.startDate + 'T00:00:00').toLocaleDateString()}
                        </span>
                    ) : collab.endDate ? (
                        <span className={styles.cardDate}>
                            <strong>End:</strong> {new Date(collab.endDate + 'T00:00:00').toLocaleDateString()}
                        </span>
                    ) : (
                        <span className={styles.cardDate}>
                            <strong>Started:</strong> {new Date(collab.createdAt).toLocaleDateString()}
                        </span>
                    )}
                </div>
                <div className={styles.cardActions}>
                    {isAdmin && onAdminComplete && (
                        collab.status === 'completed' ? (
                            <button
                                className={styles.adminOverrideBtn}
                                onClick={(e) => { e.stopPropagation(); onAdminComplete(collab, true); }}
                                disabled={adminBusy}
                                title="Reopen this collaboration (admin)"
                            >
                                {adminBusy ? 'Working…' : 'Reopen'}
                            </button>
                        ) : (
                            <button
                                className={styles.adminOverrideBtn}
                                onClick={(e) => { e.stopPropagation(); onAdminComplete(collab, false); }}
                                disabled={adminBusy}
                                title="Mark this collaboration complete (admin override)"
                            >
                                {adminBusy ? 'Working…' : 'Mark complete (override)'}
                            </button>
                        )
                    )}
                    {canNudge && (
                        alreadySent ? (
                            <span
                                className={styles.nudgeSent}
                                title={lastSentAt ? `Reminder sent ${new Date(lastSentAt).toLocaleString()}` : 'Reminder sent'}
                            >
                                Reminder sent
                            </span>
                        ) : (
                            <button
                                className={styles.nudgeBtn}
                                onClick={(e) => { e.stopPropagation(); onNudge(collab, primaryTask); }}
                                disabled={nudgeBusy}
                                title={nudgeError || `Remind ${partnerName} — they ${primaryTask.hint}`}
                            >
                                {nudgeBusy ? 'Sending…' : 'Nudge'}
                            </button>
                        )
                    )}
                    {onRemove && (
                        <button
                            className={styles.cardTrashBtn}
                            onClick={(e) => { e.stopPropagation(); onRemove(collab); }}
                            title="Delete or decline this collaboration"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                    <Icon name="arrow-right" size="16" />
                </div>
            </div>
        </div>
    );
};

// Drag-and-drop wrapper for a collab card. Used only in the "Custom" sort mode
// so the user can manually reorder their active collabs. A dedicated grip
// handle owns the drag listeners so clicking the card still opens the collab.
const SortableCollabCard = ({ collab, onClick, onRemove, onNudge, nudgeState, onRoleSwitched }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: collab.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 20 : 'auto',
    };

    const handle = (
        <button
            type="button"
            ref={setActivatorNodeRef}
            className={styles.dragHandle}
            onClick={(e) => e.stopPropagation()}
            title="Drag to reorder"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
        >
            <GripVertical size={16} />
        </button>
    );

    return (
        <div ref={setNodeRef} style={style}>
            <CollabCard collab={collab} onClick={onClick} onRemove={onRemove} onNudge={onNudge} nudgeState={nudgeState} dragHandle={handle} onRoleSwitched={onRoleSwitched} />
        </div>
    );
};

const CollabRemoveModal = ({ collab, onDelete, onDecline, onClose, deleting, declining }) => {
    const partner = collab?.partners?.[0];
    const partnerName = partner?.handle ? displayHandle(partner.handle) : partner?.firstName || 'your collaborator';

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.modalTitle}>What would you like to do?</h3>
                <p className={styles.modalDesc}>
                    This collaboration with <strong>{partnerName}</strong> can be declined or permanently deleted.
                </p>

                <div className={styles.modalOption}>
                    <h4>Decline</h4>
                    <p>End this collaboration for both parties. It will be moved to your Declined tab for reference.</p>
                    <button
                        className={styles.modalBtnDecline}
                        onClick={() => onDecline(collab)}
                        disabled={declining}
                    >
                        {declining ? 'Declining...' : 'Decline Collaboration'}
                    </button>
                </div>

                <div className={styles.modalOption}>
                    <h4>Delete permanently</h4>
                    <p>This will remove the collaboration for <strong>both</strong> parties. All expectations, deliverables, and progress will be lost. <strong>This action is irreversible.</strong></p>
                    <button
                        className={styles.modalBtnDelete}
                        onClick={() => onDelete(collab)}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete Forever'}
                    </button>
                </div>

                <button className={styles.modalBtnCancel} onClick={onClose}>
                    Cancel
                </button>
            </div>
        </div>
    );
};

// Confirmation before permanently deleting an inbox request. Deletion is a
// hard delete for both parties — unlike decline, nothing is kept or notified.
const RequestDeleteModal = ({ request, onConfirm, onClose, deleting }) => {
    const user = request?.otherUser;
    const name = user?.handle ? displayHandle(user.handle) : user?.firstName || 'this user';

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.modalTitle}>Delete this request?</h3>
                <p className={styles.modalDesc}>
                    This will permanently delete the request from <strong>{name}</strong> for
                    both parties. They won't be notified, and the request can't be restored.{' '}
                    <strong>This action is irrecoverable.</strong>
                </p>
                <button
                    className={styles.modalBtnDelete}
                    onClick={() => onConfirm(request)}
                    disabled={deleting}
                >
                    <Trash2 size={14} />
                    {deleting ? 'Deleting…' : 'Delete Forever'}
                </button>
                <button className={styles.modalBtnCancel} onClick={onClose}>
                    Cancel
                </button>
            </div>
        </div>
    );
};

// How many days a creator waits before the one-time nudge unlocks (PRD 2).
const NUDGE_ELIGIBLE_AFTER_DAYS = 7;

const RequestCard = ({ request, isIncoming, onAccept, onDecline, onMessage, onNudge, onReapply, onDelete }) => {
    const user = request.otherUser;
    const snapshot = request.snapshot || {};
    const [isAccepting, setIsAccepting] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const [isNudging, setIsNudging] = useState(false);
    const [isReapplying, setIsReapplying] = useState(false);
    // Local flag so the button flips to "Nudge sent" without a refetch.
    const [nudgeSentLocally, setNudgeSentLocally] = useState(false);

    const handleAccept = async () => {
        if (isAccepting || isDeclining) return;
        setIsAccepting(true);
        try {
            await onAccept?.(request);
        } finally {
            setIsAccepting(false);
        }
    };

    const handleDecline = async () => {
        if (isAccepting || isDeclining) return;
        setIsDeclining(true);
        try {
            await onDecline?.(request);
        } finally {
            setIsDeclining(false);
        }
    };

    const handleNudge = async () => {
        if (isNudging) return;
        setIsNudging(true);
        try {
            const ok = await onNudge?.(request);
            if (ok) setNudgeSentLocally(true);
        } finally {
            setIsNudging(false);
        }
    };

    const handleReapply = async () => {
        if (isReapplying) return;
        setIsReapplying(true);
        try {
            await onReapply?.(request);
        } finally {
            setIsReapplying(false);
        }
    };

    const isExpired = request.status === 'expired';
    const isWaitlistedOutgoing = !isIncoming && request.status === 'waitlisted';

    // Expiry countdown chip on outgoing pending applications (PRD 1).
    let expiryLabel = null;
    if (!isIncoming && request.status === 'pending' && request.expiresAt) {
        const daysLeft = Math.ceil(
            (new Date(request.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );
        expiryLabel = daysLeft > 0
            ? `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`
            : 'Expires today';
    }

    // Nudge eligibility (PRD 2): outgoing, pending, apply-gate, 7+ days old,
    // never nudged. One per application, ever.
    const ageDays = (Date.now() - new Date(request.createdAt).getTime()) / (1000 * 60 * 60 * 24);
    const nudgeAlreadySent = nudgeSentLocally || !!request.nudgedAt;
    const canNudge =
        !isIncoming &&
        typeof onNudge === 'function' &&
        request.status === 'pending' &&
        !request.channelId &&
        ageDays >= NUDGE_ELIGIBLE_AFTER_DAYS &&
        !nudgeAlreadySent;

    return (
        <div className={cn(styles.requestCard, { [styles.incoming]: isIncoming })}>
            <div className={styles.requestHeader}>
                {user?.avatarUrl ? (
                    <img 
                        src={user.avatarUrl} 
                        alt={user.firstName || 'User'} 
                        className={styles.partnerAvatar}
                    />
                ) : (
                    <div className={styles.partnerAvatarPlaceholder}>
                        <Icon name="user" size="20" />
                    </div>
                )}
                <div className={styles.requestInfo}>
                    <h4 className={styles.requestTitle}>
                        {isIncoming ? 'Request from' : 'Request to'}{' '}
                        {user?.handle ? displayHandle(user.handle) : user?.firstName || 'User'}
                    </h4>
                    <p className={styles.requestMeta}>
                        Sent {new Date(request.createdAt).toLocaleDateString()}
                    </p>
                </div>
                {isExpired ? (
                    <span style={{
                        padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 600,
                        background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb',
                    }}>
                        Expired
                    </span>
                ) : isWaitlistedOutgoing ? (
                    <span style={{
                        padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 600,
                        background: '#ffffff', color: '#0f084b', border: '1.5px solid #0f084b',
                    }}>
                        Waitlisted
                    </span>
                ) : (
                    <span className={cn(styles.directionBadge, { [styles.outgoing]: !isIncoming })}>
                        {isIncoming ? 'Incoming' : 'Sent'}
                    </span>
                )}
            </div>
            
            {(request.property || snapshot?.title) && (
                <div className={styles.requestDetails}>
                    {request.property ? (
                        <PropertyChip property={request.property} />
                    ) : (
                        <span>Regarding: {snapshot.title}</span>
                    )}
                </div>
            )}

            {expiryLabel && (
                <div style={{ margin: '4px 0' }}>
                    <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: '100px',
                        fontSize: '12px', fontWeight: 600, background: '#f3f4f6', color: '#666',
                    }}>
                        {expiryLabel}
                    </span>
                </div>
            )}

            {isWaitlistedOutgoing && (
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0' }}>
                    The host saved your application and will reach out when they're ready.
                </p>
            )}

            {isExpired && (
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0' }}>
                    It looks like this host isn't available right now — feel free to reapply another time.
                </p>
            )}
            
            <div className={styles.requestActions}>
                {isIncoming ? (
                    <>
                        <button 
                            className={cn(styles.acceptBtn, { [styles.loading]: isAccepting })}
                            onClick={handleAccept}
                            disabled={isAccepting || isDeclining}
                        >
                            {isAccepting ? 'Exploring...' : 'Explore'}
                        </button>
                        <button 
                            className={cn(styles.declineBtn, { [styles.loading]: isDeclining })}
                            onClick={handleDecline}
                            disabled={isAccepting || isDeclining}
                        >
                            {isDeclining ? 'Declining...' : 'Decline'}
                        </button>
                        {onDelete && (
                            <button
                                type="button"
                                className={styles.deleteRequestBtn}
                                onClick={() => onDelete(request)}
                                disabled={isAccepting || isDeclining}
                                title="Delete permanently"
                                aria-label="Delete request permanently"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                    </>
                ) : isExpired ? (
                    onReapply && (
                        <button
                            onClick={handleReapply}
                            disabled={isReapplying}
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                padding: '9px 20px', borderRadius: '100px', fontSize: '13.5px', fontWeight: 600,
                                background: '#0f084b', color: '#ffffff', border: '1.5px solid #0f084b',
                                cursor: isReapplying ? 'default' : 'pointer', opacity: isReapplying ? 0.6 : 1,
                            }}
                        >
                            {isReapplying ? 'Reapplying…' : 'Reapply'}
                        </button>
                    )
                ) : (
                    <>
                        {request.channelId && (
                            <button 
                                className={styles.messageBtn}
                                onClick={() => onMessage?.(request)}
                            >
                                <Icon name="message" size="16" />
                                View in Messages
                            </button>
                        )}
                        {canNudge && (
                            <button
                                onClick={handleNudge}
                                disabled={isNudging}
                                title="Hosts sometimes miss applications — one friendly reminder is fair game."
                                style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '9px 20px', borderRadius: '100px', fontSize: '13.5px', fontWeight: 600,
                                    background: '#ffffff', color: '#0f084b', border: '1.5px solid #0f084b',
                                    cursor: isNudging ? 'default' : 'pointer', opacity: isNudging ? 0.6 : 1,
                                }}
                            >
                                {isNudging ? 'Sending…' : 'Send a nudge'}
                            </button>
                        )}
                        {!isIncoming && nudgeAlreadySent && request.status === 'pending' && (
                            <span style={{ fontSize: '13px', color: '#9ca3af', alignSelf: 'center' }}>
                                Nudge sent{request.nudgedAt ? ` ${new Date(request.nudgedAt).toLocaleDateString()}` : ''}
                            </span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const DraftCard = ({ draft, onClick, onDelete }) => {
    const recipient = draft.recipient;
    const terms = draft.terms || {};
    const [isDeleting, setIsDeleting] = useState(false);

    const displayTitle = draft.title ||
        (recipient?.handle ? displayHandle(recipient.handle) : recipient?.firstName) ||
        'Untitled Draft';

    const handleDelete = async (e) => {
        e.stopPropagation();
        if (isDeleting) return;
        if (!window.confirm('Delete this draft?')) return;
        setIsDeleting(true);
        try {
            await onDelete?.(draft);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div
            className={styles.collabCard}
            onClick={onClick}
            style={{ borderLeftColor: '#f59e0b' }}
        >
            <div className={styles.cardHeader}>
                {recipient?.avatarUrl ? (
                    <img
                        src={recipient.avatarUrl}
                        alt={recipient.firstName || 'Recipient'}
                        className={styles.partnerAvatar}
                    />
                ) : (
                    <div className={styles.partnerAvatarPlaceholder}>
                        <Icon name="user" size="20" />
                    </div>
                )}
                <div className={styles.cardInfo}>
                    <h4 className={styles.cardTitle}>{displayTitle}</h4>
                    <p className={styles.cardPartner}>
                        {recipient
                            ? `to ${recipient.handle ? displayHandle(recipient.handle) : recipient.firstName || 'User'}`
                            : 'No recipient yet'}
                    </p>
                </div>
                <span
                    className={styles.statusBadge}
                    style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        borderColor: '#f59e0b'
                    }}
                >
                    draft
                </span>
            </div>

            <PropertyChip property={draft.property} />

            {terms.compensation?.type && (
                <div className={styles.cardMeta}>
                    <Icon name="coin" size="14" />
                    <span style={{ textTransform: 'capitalize' }}>
                        {terms.compensation.type.replace(/_/g, ' ')}
                    </span>
                </div>
            )}

            <div className={styles.cardFooter}>
                <span className={styles.cardDate}>
                    Edited {new Date(draft.updatedAt).toLocaleDateString()}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                        className={styles.draftDeleteBtn}
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        <Icon name="trash" size="14" />
                    </button>
                    <Icon name="arrow-right" size="16" />
                </div>
            </div>
        </div>
    );
};

const BoardPostCard = ({ post, onDeactivate, onEdit }) => {
    const [deactivating, setDeactivating] = useState(false);
    const label = post.slug ? `@${post.slug}` : post.name || 'My Post';
    const isExpired = post.collabPostExpiresAt && new Date(post.collabPostExpiresAt) < new Date();
    const statusLabel = post.collabStatus === 'completed' ? 'Completed'
        : post.collabStatus === 'pending' ? 'Waitlist'
        : isExpired ? 'Expired' : 'Active';
    const statusStyle = statusLabel === 'Active'
        ? { bg: '#ecfdf5', border: '#10b981', text: '#065f46' }
        : statusLabel === 'Waitlist'
        ? { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' }
        : { bg: '#f1f5f9', border: '#94a3b8', text: '#64748b' };

    const handleDeactivate = async (e) => {
        e.stopPropagation();
        if (deactivating) return;
        if (!window.confirm('Deactivate this collab post? It will be removed from the board.')) return;
        setDeactivating(true);
        try {
            await onDeactivate?.(post);
        } finally {
            setDeactivating(false);
        }
    };

    return (
        <div className={styles.boardPostCard} onClick={() => onEdit?.(post)}>
            <div className={styles.boardPostHeader}>
                {post.imageUrl ? (
                    <img src={post.imageUrl} alt={label} className={styles.boardPostImg} />
                ) : (
                    <div className={styles.boardPostImgPlaceholder}>
                        <Icon name={post.type === 'creator' ? 'user' : 'home'} size="20" />
                    </div>
                )}
                <div className={styles.boardPostInfo}>
                    <h4 className={styles.boardPostName}>{label}</h4>
                    {post.location && <p className={styles.boardPostLocation}>{post.location}</p>}
                </div>
                <span
                    className={styles.statusBadge}
                    style={{ backgroundColor: statusStyle.bg, color: statusStyle.text, borderColor: statusStyle.border }}
                >
                    {statusLabel}
                </span>
            </div>
            <div className={styles.boardPostActions}>
                <button className={styles.boardPostEditBtn} onClick={(e) => { e.stopPropagation(); onEdit?.(post); }}>
                    <Icon name="edit" size="14" />
                    Edit Post
                </button>
                {statusLabel !== 'Completed' && (
                    <button
                        className={styles.boardPostDeactivateBtn}
                        onClick={handleDeactivate}
                        disabled={deactivating}
                    >
                        {deactivating ? 'Deactivating...' : 'Deactivate'}
                    </button>
                )}
            </div>
        </div>
    );
};

const Collabs = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState(() => {
        const tab = searchParams.get('tab');
        // Backward compat: old links to ?tab=pending land on the unified
        // Inbox. ?tab=waitlist is a real destination again (PRD 3) — the
        // waitlist got promoted to a top-level tab.
        if (tab === 'pending') return 'inbox';
        return ['active', 'inbox', 'drafts', 'completed', 'declined', 'waitlist', 'admin-all'].includes(tab) ? tab : 'active';
    });
    const [isExploreModalOpen, setIsExploreModalOpen] = useState(false);
    const [membershipTier, setMembershipTier] = useState(null);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [declining, setDeclining] = useState(false);
    // Inbox request pending permanent deletion (confirmation modal target).
    const [deleteRequestTarget, setDeleteRequestTarget] = useState(null);
    const [deletingRequest, setDeletingRequest] = useState(false);
    // Admin override (all-collabs) view
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminData, setAdminData] = useState(null);
    const [adminLoading, setAdminLoading] = useState(false);
    const [adminError, setAdminError] = useState(null);
    const [adminBusyId, setAdminBusyId] = useState(null);
    // Per-collab nudge UI state, keyed by collab id: { busy, sent, lastSentAt, error }
    const [nudgeStateById, setNudgeStateById] = useState({});
    // Transient confirmation banner after waitlisting an applicant (PRD 3).
    const [waitlistToast, setWaitlistToast] = useState(null);

    // Merged host+creator accounts pick which side of the collab they're on
    // before accepting; single-persona users skip the modal entirely.
    const { roles: personaRoles, activePersona } = usePersona();
    const isDualPersona = personaRoles.includes('creator') && personaRoles.includes('host');
    const [personaPickerRequest, setPersonaPickerRequest] = useState(null);

    // 'Recent Activity' is the default: collabs with the newest messages,
    // deliverable updates, or status changes float to the top.
    const SORT_OPTIONS = ['Recent Activity', 'Newest First', 'Oldest First', 'Start Date', 'Partner A–Z', 'Custom'];

    const [filterProperty, setFilterProperty] = useState(() => searchParams.get('property') || '');
    const [sortOption, setSortOption] = useState(() => {
        const s = searchParams.get('sort');
        return SORT_OPTIONS.includes(s) ? s : SORT_OPTIONS[0];
    });

    // Local copy of the active collabs used for optimistic manual reordering in
    // "Custom" sort mode. Seeded from the server response (already ordered by
    // the user's persisted sort_order).
    const [activeOrder, setActiveOrder] = useState([]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    
    const email = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_email') || '' : '';
    const userType = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_user_type') || 'host' : 'host';

    useEffect(() => {
        if (!email) {
            navigate(userType === 'creator' ? '/creator-login' : '/host-login');
            return;
        }

        (async () => {
            try {
                const res = await fetch('/api/access/subscription', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });
                const accessData = await res.json();
                if (res.ok && accessData.ok) {
                    setIsAdmin(Array.isArray(accessData.roles) && accessData.roles.includes('admin'));
                }
                if (res.ok && accessData.ok && accessData.active) {
                    setMembershipTier(accessData.membershipTier || 'all_access');
                    if (accessData.membershipTier === 'basic') {
                        setLoading(false);
                        return;
                    }
                }
            } catch (_) {}
            fetchCollabs();
        })();
    }, [email, userType, navigate]);

    const fetchCollabs = async () => {
        setLoading(true);
        setError(null);
        
        const token = getAuthToken();
        if (!token) {
            setError('Please log in to view your collaborations');
            setLoading(false);
            return;
        }

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/list`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const result = await response.json();

            if (result.ok) {
                setData(result);
            } else {
                setError(result.error || 'Failed to load collaborations');
            }
        } catch (err) {
            console.error('Error fetching collabs:', err);
            setError('Failed to load collaborations');
        } finally {
            setLoading(false);
        }
    };

    const fetchAdminCollabs = async () => {
        setAdminLoading(true);
        setAdminError(null);

        const token = getAuthToken();
        if (!token) {
            setAdminError('Please log in to view all collaborations');
            setAdminLoading(false);
            return;
        }

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/list?scope=all`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const result = await response.json();
            if (result.ok) {
                setAdminData(result);
            } else {
                setAdminError(result.error || 'Failed to load collaborations');
            }
        } catch (err) {
            console.error('Error fetching admin collabs:', err);
            setAdminError('Failed to load collaborations');
        } finally {
            setAdminLoading(false);
        }
    };

    // Load the admin all-collabs list when the admin opens that tab.
    useEffect(() => {
        if (isAdmin && activeTab === 'admin-all' && !adminData && !adminLoading) {
            fetchAdminCollabs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, activeTab]);

    const handleAdminComplete = async (collab, revert) => {
        const token = getAuthToken();
        if (!token) return;

        const verb = revert ? 'reopen' : 'mark complete (override)';
        if (!window.confirm(`Are you sure you want to ${verb} this collaboration?`)) return;

        let reason = null;
        if (!revert) {
            reason = window.prompt('Optional reason for this override (leave blank to skip):', '') || null;
        }

        setAdminBusyId(collab.id);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/admin-complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ collaborationId: collab.id, revert: !!revert, reason }),
            });
            const result = await response.json();
            if (!result.ok) {
                alert('Failed: ' + (result.error || 'Unknown error'));
                return;
            }
            await fetchAdminCollabs();
            // Keep the member-facing list in sync if it's already loaded.
            fetchCollabs();
        } catch (err) {
            console.error('Error overriding collab:', err);
            alert('Failed to update collaboration');
        } finally {
            setAdminBusyId(null);
        }
    };

    const handleNudge = async (collab, task) => {
        const token = getAuthToken();
        if (!token) return;

        setNudgeStateById(prev => ({
            ...prev,
            [collab.id]: { ...(prev[collab.id] || {}), busy: true, error: null },
        }));

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/nudge`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ collaborationId: collab.id, taskType: task?.type }),
            });
            const result = await response.json();

            if (response.ok && result.ok) {
                setNudgeStateById(prev => ({
                    ...prev,
                    [collab.id]: { busy: false, sent: true, lastSentAt: result.lastSentAt, error: null },
                }));
            } else if (response.status === 409 && result.error === 'rate_limited') {
                // Already nudged within the cooldown window — reflect "sent" state.
                setNudgeStateById(prev => ({
                    ...prev,
                    [collab.id]: { busy: false, sent: true, lastSentAt: result.lastSentAt, error: result.message || null },
                }));
            } else {
                setNudgeStateById(prev => ({
                    ...prev,
                    [collab.id]: { busy: false, sent: false, error: result.error || 'Failed to send reminder' },
                }));
            }
        } catch (err) {
            console.error('Error sending nudge:', err);
            setNudgeStateById(prev => ({
                ...prev,
                [collab.id]: { busy: false, sent: false, error: 'Failed to send reminder' },
            }));
        }
    };

    const handleCollabClick = (collab) => {
        navigate(`/collabs/workspace?id=${collab.id}`);
    };

    const handleAcceptRequest = async (request, persona = null) => {
        const token = getAuthToken();
        if (!token) return;

        // Both-roles accounts choose which account the collab is for first.
        if (isDualPersona && !persona) {
            setPersonaPickerRequest(request);
            return;
        }
        const acceptPersona = persona
            || (activePersona === 'creator' || activePersona === 'host' ? activePersona : null);

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/requests/accept`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    requestId: request.id,
                    ...(acceptPersona ? { persona: acceptPersona } : {}),
                }),
            });

            const result = await response.json();

            if (result.ok) {
                navigate(`/collabs/workspace?id=${result.collaborationId}`);
            } else {
                alert('Failed to accept: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error accepting request:', err);
            alert('Failed to accept request');
        }
    };

    // Apply-gate "Open chat" — host approves the applicant to chat without
    // committing to the full workspace yet. Hits /requests/open-chat which
    // creates the Stream DM and transitions the request to 'chatting'.
    // If the request already has a channel (status='chatting'), we skip the
    // round-trip and jump straight to Messages.
    const handleOpenChatForApplication = async (request) => {
        if (request?.channelId) {
            navigate(`/messages?channelId=${encodeURIComponent(request.channelId)}`);
            return;
        }
        const token = getAuthToken();
        if (!token) return;
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const res = await fetch(`${apiBase}/api/collab/requests/open-chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ requestId: request.id }),
            });
            const result = await res.json();
            if (!result?.ok) {
                alert('Failed to open chat: ' + (result?.error || 'Unknown error'));
                return;
            }
            if (result.channelId) {
                navigate(`/messages?channelId=${encodeURIComponent(result.channelId)}`);
            } else {
                navigate('/messages');
            }
        } catch (err) {
            console.error('Error opening chat:', err);
            alert('Failed to open chat');
        }
    };

    // Apply-gate "Add to waitlist" — polite defer; no DM created. We refresh
    // the list so the row jumps out of Pending into the Waitlist tab, and
    // show a confirmation that tells the host where to find them (PRD 3).
    const handleWaitlistRequest = async (request) => {
        const token = getAuthToken();
        if (!token) return;
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const res = await fetch(`${apiBase}/api/collab/requests/waitlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ requestId: request.id }),
            });
            const result = await res.json();
            if (!result?.ok) {
                alert('Failed to add to waitlist: ' + (result?.error || 'Unknown error'));
                return;
            }
            const name = request?.otherUser?.handle
                ? displayHandle(request.otherUser.handle)
                : (request?.otherUser?.firstName || 'Applicant');
            setWaitlistToast(`${name} added to your waitlist`);
            setTimeout(() => setWaitlistToast(null), 6000);
            fetchCollabs();
        } catch (err) {
            console.error('Error waitlisting:', err);
            alert('Failed to add to waitlist');
        }
    };

    // One-time creator nudge on a stalled application (PRD 2).
    // Returns true on success so the card can flip to "Nudge sent" locally.
    const handleNudgeApplication = async (request) => {
        const token = getAuthToken();
        if (!token) return false;
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const res = await fetch(`${apiBase}/api/collab/requests/nudge`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ requestId: request.id }),
            });
            const result = await res.json();
            if (res.ok && result?.ok) return true;
            if (result?.error === 'already_nudged') return true;
            alert('Failed to send nudge: ' + (result?.error || 'Unknown error'));
            return false;
        } catch (err) {
            console.error('Error nudging application:', err);
            alert('Failed to send nudge');
            return false;
        }
    };

    // One-click reapply on an expired application (PRD 1). Creates a fresh
    // request from the old snapshot; the expired row stays for history.
    const handleReapply = async (request) => {
        const token = getAuthToken();
        if (!token) return;
        const recipientUserId = request?.otherUser?.userId;
        if (!recipientUserId) {
            alert('This host is no longer available.');
            return;
        }
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const res = await fetch(`${apiBase}/api/collab/requests/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    recipientUserId,
                    channelId: null,
                    targetKind: request.targetKind || null,
                    targetId: request.targetId || null,
                    snapshot: request.snapshot || null,
                }),
            });
            const result = await res.json();
            if (!res.ok || !result?.ok) {
                alert('Failed to reapply: ' + (result?.error || 'Unknown error'));
                return;
            }
            fetchCollabs();
        } catch (err) {
            console.error('Error reapplying:', err);
            alert('Failed to reapply');
        }
    };

    const handleDeclineRequest = async (request) => {
        const token = getAuthToken();
        if (!token) return;

        if (!window.confirm('Are you sure you want to decline this collaboration request?')) {
            return;
        }

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/requests/decline`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ requestId: request.id }),
            });

            const result = await response.json();

            if (result.ok) {
                fetchCollabs(); // Refresh the list
            } else {
                alert('Failed to decline: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error declining request:', err);
            alert('Failed to decline request');
        }
    };

    // Permanently delete an inbox request. Confirmation happens in
    // RequestDeleteModal, so no window.confirm here.
    const handleDeleteRequest = async (request) => {
        const token = getAuthToken();
        if (!token) return;
        setDeletingRequest(true);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/requests/delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ requestId: request.id }),
            });
            const result = await response.json();
            if (result.ok) {
                setDeleteRequestTarget(null);
                fetchCollabs();
            } else {
                alert('Failed to delete: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error deleting request:', err);
            alert('Failed to delete request');
        } finally {
            setDeletingRequest(false);
        }
    };

    const handleMessageRequest = (request) => {
        if (request.channelId) {
            navigate(`/messages`);
        }
    };

    const handleExploreCollab = (recipientUserId, selectedUser) => {
        const params = new URLSearchParams();
        params.set('to', recipientUserId);
        params.set('collabRequest', 'true');

        if (selectedUser.resultType === 'property' && selectedUser.propertyId) {
            params.set('targetKind', 'property');
            params.set('targetId', selectedUser.propertyId);
            const title = selectedUser.propertyName || selectedUser.propertySlug || '';
            if (title) params.set('title', title);
        }

        setIsExploreModalOpen(false);
        window.location.href = `/messages?${params.toString()}`;
    };

    const handleDraftClick = (draft) => {
        navigate(`/collabs/draft?id=${draft.id}`);
    };

    const handleDeleteDraft = async (draft) => {
        const token = getAuthToken();
        if (!token) return;

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/drafts/delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ draftId: draft.id }),
            });

            const result = await response.json();
            if (result.ok) {
                fetchCollabs();
            } else {
                alert('Failed to delete draft');
            }
        } catch (err) {
            console.error('Error deleting draft:', err);
            alert('Failed to delete draft');
        }
    };

    const handleStartDraft = async (recipientUserId, selectedUser) => {
        setIsExploreModalOpen(false);

        // If no recipient (blank draft), navigate directly
        if (!recipientUserId) {
            navigate('/collabs/draft');
            return;
        }

        // Create draft with recipient, then navigate
        const token = getAuthToken();
        if (!token) return;

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const body = { recipientUserId };

            if (selectedUser?.resultType === 'property' && selectedUser?.propertyId) {
                body.targetKind = 'property';
                body.targetId = selectedUser.propertyId;
                body.title = selectedUser.propertyName || selectedUser.propertySlug || '';
            }

            const response = await fetch(`${apiBase}/api/collab/drafts/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });

            const result = await response.json();
            if (result.ok) {
                navigate(`/collabs/draft?id=${result.draftId}`);
            } else {
                alert('Failed to create draft');
            }
        } catch (err) {
            console.error('Error creating draft:', err);
            alert('Failed to create draft');
        }
    };

    const handleDeleteCollab = async (collab) => {
        const token = getAuthToken();
        if (!token) return;
        setDeleting(true);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/destroy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ collaborationId: collab.id }),
            });
            const result = await response.json();
            if (result.ok) {
                setRemoveTarget(null);
                fetchCollabs();
            } else {
                alert(result.error || 'Failed to delete collaboration');
            }
        } catch (err) {
            console.error('Error deleting collab:', err);
            alert('Failed to delete collaboration');
        } finally { setDeleting(false); }
    };

    const handleDeclineCollab = async (collab) => {
        const token = getAuthToken();
        if (!token) return;
        setDeclining(true);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/decline`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ collaborationId: collab.id }),
            });
            const result = await response.json();
            if (result.ok) {
                setRemoveTarget(null);
                fetchCollabs();
            } else {
                alert(result.error || 'Failed to decline collaboration');
            }
        } catch (err) {
            console.error('Error declining collab:', err);
            alert('Failed to decline collaboration');
        } finally { setDeclining(false); }
    };

    const handleDeactivatePost = async (post) => {
        const token = getAuthToken();
        if (!token) return;
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const body = post.type === 'property'
                ? { type: 'property', propertyId: post.id, deactivate: true }
                : { type: 'profile', profileId: post.id, deactivate: true };
            const response = await fetch(`${apiBase}/api/collab/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            const result = await response.json();
            if (result.ok) {
                fetchCollabs();
            } else {
                alert(result.error || 'Failed to deactivate post');
            }
        } catch (err) {
            console.error('Error deactivating post:', err);
            alert('Failed to deactivate post');
        }
    };

    const handleEditPost = (post) => {
        const params = new URLSearchParams();
        if (post.type === 'property') {
            params.set('editProperty', post.id);
        } else {
            params.set('editCreator', post.id);
        }
        navigate(`/collab-board?${params.toString()}`);
    };

    // Sync filter/sort/tab to URL params
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (activeTab && activeTab !== 'active') next.set('tab', activeTab);
        else next.delete('tab');
        if (filterProperty) next.set('property', filterProperty);
        else next.delete('property');
        if (sortOption && sortOption !== SORT_OPTIONS[0]) next.set('sort', sortOption);
        else next.delete('sort');
        const current = searchParams.toString();
        const updated = next.toString();
        if (current !== updated) setSearchParams(next, { replace: true });
    }, [activeTab, filterProperty, sortOption]);

    // Keep the manual-reorder working copy in sync with the latest server data.
    useEffect(() => {
        setActiveOrder(data?.grouped?.active || []);
    }, [data]);

    // Persist the current manual ordering for this user. On failure we resync
    // from the server (mirrors the deliverables reorder behavior).
    const persistOrder = async (orderedIds) => {
        const token = getAuthToken();
        if (!token) return;
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const res = await fetch(`${apiBase}/api/collab/reorder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ itemIds: orderedIds }),
            });
            if (!res.ok) {
                fetchCollabs();
            }
        } catch (_) {
            fetchCollabs();
        }
    };

    // Derive unique property options from all collabs
    const propertyOptions = useMemo(() => {
        if (!data) return [];
        const map = new Map();
        const addProp = (item) => {
            if (item?.property?.id && !map.has(String(item.property.id))) {
                const p = item.property;
                map.set(String(p.id), p.slug ? `@${p.slug}` : p.name || p.location || `Property ${p.id}`);
            }
        };
        (data.collaborations || []).forEach(addProp);
        (data.pendingRequests || []).forEach(addProp);
        (data.waitlistedRequests || []).forEach(addProp);
        (data.drafts || []).forEach(addProp);
        return [['', 'All Properties'], ...Array.from(map.entries())];
    }, [data]);

    const applyFilterSort = (items) => {
        let list = [...items];
        if (filterProperty) {
            list = list.filter(item => item.property && String(item.property.id) === filterProperty);
        }
        list.sort((a, b) => {
            switch (sortOption) {
                case 'Newest First':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case 'Oldest First':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case 'Start Date':
                    return (a.startDate || '9999') < (b.startDate || '9999') ? -1 : 1;
                case 'Partner A–Z': {
                    const aName = (a.partners?.[0]?.handle || a.partners?.[0]?.firstName || '').toLowerCase();
                    const bName = (b.partners?.[0]?.handle || b.partners?.[0]?.firstName || '').toLowerCase();
                    return aName.localeCompare(bName);
                }
                default:
                    // 'Recent Activity' — whatever changed most recently
                    // (messages, deliverables, status) floats to the top.
                    return new Date(b.lastActivityAt || b.createdAt) - new Date(a.lastActivityAt || a.createdAt);
            }
        });
        return list;
    };

    const isCustomSort = sortOption === 'Custom';

    // In Custom mode we preserve the user's manual order (applying only the
    // property filter); otherwise we fall back to the chosen sort.
    const displayedActive = isCustomSort
        ? (filterProperty
            ? activeOrder.filter(item => item.property && String(item.property.id) === filterProperty)
            : activeOrder)
        : applyFilterSort(data?.grouped?.active || []);

    const handleCollabDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = displayedActive.findIndex(c => c.id === active.id);
        const newIndex = displayedActive.findIndex(c => c.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        // Reorder the visible subset, then merge back into the full list so a
        // property filter doesn't drop hidden collabs from the persisted order.
        const newDisplayedIds = arrayMove(displayedActive, oldIndex, newIndex).map(c => c.id);
        const displayedSet = new Set(newDisplayedIds);
        const queue = [...newDisplayedIds];
        const byId = new Map(activeOrder.map(c => [c.id, c]));
        const nextIds = activeOrder.map(c => (displayedSet.has(c.id) ? queue.shift() : c.id));
        const nextObjs = nextIds.map(id => byId.get(id));

        setActiveOrder(nextObjs);
        persistOrder(nextIds);
    };

    const applyRequestFilter = (items) => {
        if (!filterProperty) return items;
        return items.filter(item => item.property && String(item.property.id) === filterProperty);
    };

    // Inbox = everything awaiting a decision: truly pending + chatting, both
    // directions. Waitlisted INCOMING applications live in their own
    // top-level Waitlist tab (PRD 3); waitlisted OUTGOING stays in "You sent"
    // since it's a status of an application the creator is tracking.
    const pendingIncoming    = data?.counts?.pendingIncoming    || 0;
    const pendingOutgoing    = data?.counts?.pendingOutgoing    || 0;
    const chattingIncoming   = data?.counts?.chattingIncoming   || 0;
    const chattingOutgoing   = data?.counts?.chattingOutgoing   || 0;
    const waitlistedIncoming = data?.counts?.waitlistedIncoming || 0;
    const waitlistedOutgoing = data?.counts?.waitlistedOutgoing || 0;

    const inboxTotal =
        pendingIncoming + pendingOutgoing +
        chattingIncoming + chattingOutgoing +
        waitlistedOutgoing;
    // Only fire the alert dot on genuinely new applications — chatting and
    // waitlisted have already been triaged by the host.
    const inboxHasAlert = pendingIncoming > 0;

    const tabs = [
        { id: 'active',    label: 'Active',    count: data?.counts?.active || 0 },
        { id: 'inbox',     label: 'Inbox',     count: inboxTotal, hasAlert: inboxHasAlert },
        { id: 'waitlist',  label: 'Waitlist',  count: waitlistedIncoming },
        { id: 'drafts',    label: 'Drafts',    count: data?.counts?.drafts || 0 },
        { id: 'completed', label: 'Completed', count: data?.counts?.completed || 0 },
        { id: 'declined',  label: 'Declined',  count: data?.counts?.declined || 0 },
        ...(isAdmin ? [{ id: 'admin-all', label: 'All Collabs (Admin)', count: adminData?.collaborations?.length || 0 }] : []),
    ];

    // Inbox sub-sections. Apply the property filter to each one so the
    // filter bar at the top of the page works across the whole inbox.
    // "Awaiting your review" sorts by soonest expiry so the applications
    // closest to timing out surface at the top (PRD 1).
    const incomingNew = applyRequestFilter((data?.pendingRequests || []).filter(r => r.direction === 'incoming'))
        .slice()
        .sort((a, b) => {
            const aExp = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
            const bExp = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
            return aExp - bExp;
        });
    const incomingChatting    = applyRequestFilter((data?.chattingRequests   || []).filter(r => r.direction === 'incoming'));
    // Waitlist tab content — oldest first, so the list doubles as a "who
    // have I kept waiting longest" view.
    const incomingWaitlisted  = applyRequestFilter((data?.waitlistedRequests || []).filter(r => r.direction === 'incoming'))
        .slice()
        .sort((a, b) => new Date(a.waitlistedAt || a.createdAt) - new Date(b.waitlistedAt || b.createdAt));
    // "You sent" is the union of every outgoing app still in flight,
    // regardless of which sub-state the host parked it in — plus recently
    // expired ones so the creator gets their Reapply path (PRD 1).
    const outgoingInFlight = applyRequestFilter([
        ...(data?.pendingRequests    || []).filter(r => r.direction === 'outgoing'),
        ...(data?.chattingRequests   || []).filter(r => r.direction === 'outgoing'),
        ...(data?.waitlistedRequests || []).filter(r => r.direction === 'outgoing'),
        ...(data?.expiredRequests    || []).filter(r => r.direction === 'outgoing'),
    ]);
    const inboxIsEmpty =
        incomingNew.length === 0 &&
        incomingChatting.length === 0 &&
        outgoingInFlight.length === 0;

    return (
        <>
        {membershipTier === 'basic' ? (
            <BlurredGate userType={userType}>
                <div className={cn("section", styles.section)}>
                    <div className={styles.container}>
                        <CollabIntroVideo wrapperClassName={styles.introVideoTopRight} />
                        <div className={styles.header}>
                            <h1 className={styles.title}>My Collab Tracker</h1>
                            <p className={styles.subtitle}>Track your collaborations</p>
                        </div>
                        <div className={styles.content}>
                            <div style={{ minHeight: '50vh' }} />
                        </div>
                    </div>
                </div>
            </BlurredGate>
        ) : (
        <div className={cn("section", styles.section)}>
                <div className={styles.container}>
                    <CollabIntroVideo wrapperClassName={styles.introVideoTopRight} />
                    <div className={styles.header}>
                        <h1 className={styles.title}>My Collab Tracker</h1>
                        <p className={styles.subtitle}>Track your collaborations</p>
                        <button
                            className={styles.exploreBtn}
                            onClick={() => setIsExploreModalOpen(true)}
                        >
                            Explore New Collab
                        </button>
                    </div>

                {data?.myBoardPosts?.length > 0 && (
                    <div className={styles.boardPostsSection}>
                        <h3 className={styles.boardPostsSectionTitle}>My Collab Board Posts</h3>
                        <div className={styles.boardPostsList}>
                            {data.myBoardPosts.map(post => (
                                <BoardPostCard
                                    key={`${post.type}-${post.id}`}
                                    post={post}
                                    onDeactivate={handleDeactivatePost}
                                    onEdit={handleEditPost}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <div className={styles.tabs}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={cn(styles.tab, { [styles.activeTab]: activeTab === tab.id })}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                            {tab.hasAlert && activeTab !== tab.id && (
                                <span className={styles.alertDot} />
                            )}
                            {tab.count > 0 && (
                                <span className={styles.tabCount}>{tab.count}</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className={styles.content}>
                    {waitlistToast && (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            flexWrap: 'wrap', gap: '12px', padding: '12px 16px', marginBottom: '14px',
                            background: '#0f084b', color: '#ffffff', borderRadius: '12px',
                            fontSize: '14px', fontWeight: 600,
                        }}>
                            <span>{waitlistToast}</span>
                            <button
                                type="button"
                                onClick={() => { setWaitlistToast(null); setActiveTab('waitlist'); }}
                                style={{
                                    padding: '6px 16px', borderRadius: '100px', border: '1.5px solid #ffffff',
                                    background: 'transparent', color: '#ffffff', fontSize: '13px',
                                    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                                }}
                            >
                                View waitlist
                            </button>
                        </div>
                    )}
                    {!loading && !error && (
                        <div className={styles.filterBar}>
                            {propertyOptions.length > 1 && (
                                <div className={styles.filterGroup}>
                                    <Dropdown
                                        className={styles.filterDropdown}
                                        value={propertyOptions.find(([id]) => id === filterProperty)?.[1] || 'All Properties'}
                                        setValue={(label) => {
                                            const match = propertyOptions.find(([, l]) => l === label);
                                            setFilterProperty(match ? match[0] : '');
                                        }}
                                        options={propertyOptions.map(([, label]) => label)}
                                    />
                                </div>
                            )}
                            <div className={styles.filterGroup}>
                                <Dropdown
                                    className={styles.sortDropdown}
                                    value={sortOption}
                                    setValue={setSortOption}
                                    options={SORT_OPTIONS}
                                />
                            </div>
                        </div>
                    )}
                    {loading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner} />
                            <p>Loading collaborations...</p>
                        </div>
                    ) : error ? (
                        <div className={styles.errorState}>
                            <Icon name="alert-triangle" size="48" />
                            <p>{error}</p>
                            <button onClick={fetchCollabs} className={styles.retryBtn}>
                                Try Again
                            </button>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'active' && (
                                <div className={styles.collabList}>
                                    {data?.grouped?.active?.length > 0 ? (
                                        displayedActive.length > 0 ? (
                                            isCustomSort ? (
                                                <DndContext
                                                    sensors={sensors}
                                                    collisionDetection={closestCenter}
                                                    onDragEnd={handleCollabDragEnd}
                                                >
                                                    <SortableContext
                                                        items={displayedActive.map(c => c.id)}
                                                        strategy={verticalListSortingStrategy}
                                                    >
                                                        {displayedActive.map(collab => (
                                                            <SortableCollabCard
                                                                key={collab.id}
                                                                collab={collab}
                                                                onClick={() => handleCollabClick(collab)}
                                                                onRemove={setRemoveTarget}
                                                                onNudge={handleNudge}
                                                                nudgeState={nudgeStateById[collab.id]}
                                                                onRoleSwitched={fetchCollabs}
                                                            />
                                                        ))}
                                                    </SortableContext>
                                                </DndContext>
                                            ) : (
                                                displayedActive.map(collab => (
                                                    <CollabCard 
                                                        key={collab.id} 
                                                        collab={collab} 
                                                        onClick={() => handleCollabClick(collab)}
                                                        onRemove={setRemoveTarget}
                                                        onNudge={handleNudge}
                                                        nudgeState={nudgeStateById[collab.id]}
                                                        onRoleSwitched={fetchCollabs}
                                                    />
                                                ))
                                            )
                                        ) : (
                                            <div className={styles.emptyState}>
                                                <Icon name="filter" size="48" />
                                                <h3>No matches</h3>
                                                <p>No active collaborations match your filter.</p>
                                                <button onClick={() => setFilterProperty('')} className={styles.ctaBtn}>
                                                    Clear Filter
                                                </button>
                                            </div>
                                        )
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Icon name="briefcase" size="48" />
                                            <h3>No active collaborations</h3>
                                            <p>When you start a collaboration, it will appear here.</p>
                                            <button 
                                                onClick={() => navigate('/collab-board')}
                                                className={styles.ctaBtn}
                                            >
                                                Browse Collab Board
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'inbox' && (
                                <div className={styles.requestList}>
                                    {/* 1. Truly new applications — top priority. Full ApplicantCard with all 4 actions. */}
                                    {incomingNew.length > 0 && (
                                        <InboxSection
                                            title="Awaiting your review"
                                            count={incomingNew.length}
                                            subtitle="New applications that haven't been triaged yet."
                                            defaultOpen
                                        >
                                            {incomingNew.map(req => (
                                                // Legacy in-flight requests (created before the apply
                                                // gate) still carry a channel_id — keep the 2-button
                                                // UI for those so we don't break old pitches.
                                                req.channelId ? (
                                                    <RequestCard
                                                        key={req.id}
                                                        request={req}
                                                        isIncoming={true}
                                                        onAccept={handleAcceptRequest}
                                                        onDecline={handleDeclineRequest}
                                                        onDelete={setDeleteRequestTarget}
                                                    />
                                                ) : (
                                                    <ApplicantCard
                                                        key={req.id}
                                                        request={req}
                                                        onOpenChat={handleOpenChatForApplication}
                                                        onStartCollab={handleAcceptRequest}
                                                        onWaitlist={handleWaitlistRequest}
                                                        onDecline={handleDeclineRequest}
                                                        onDelete={setDeleteRequestTarget}
                                                    />
                                                )
                                            ))}
                                        </InboxSection>
                                    )}

                                    {/* 2. In conversation — you've opened the chat but haven't promoted. */}
                                    {incomingChatting.length > 0 && (
                                        <InboxSection
                                            title="In conversation"
                                            count={incomingChatting.length}
                                            subtitle="You've opened the chat — promote when you're ready to commit."
                                        >
                                            {incomingChatting.map(req => (
                                                <ApplicantCard
                                                    key={req.id}
                                                    request={req}
                                                    onOpenChat={handleOpenChatForApplication}
                                                    onStartCollab={handleAcceptRequest}
                                                    onWaitlist={null}
                                                    onDecline={handleDeclineRequest}
                                                    onDelete={setDeleteRequestTarget}
                                                />
                                            ))}
                                        </InboxSection>
                                    )}

                                    {/* 3. Waitlisted applicants moved to the top-level Waitlist
                                        tab (PRD 3) — leave a pointer so hosts learn the new home. */}
                                    {waitlistedIncoming > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab('waitlist')}
                                            style={{
                                                display: 'block', width: '100%', textAlign: 'left',
                                                padding: '12px 16px', marginBottom: '14px',
                                                background: '#f8f7fd', border: '1px dashed #c9c4e4',
                                                borderRadius: '12px', fontSize: '13.5px', color: '#0f084b',
                                                cursor: 'pointer', fontWeight: 600,
                                            }}
                                        >
                                            {waitlistedIncoming} applicant{waitlistedIncoming === 1 ? '' : 's'} on your waitlist — view them in your Waitlist tab →
                                        </button>
                                    )}

                                    {/* 4. Everything you sent that's still pending a host decision. */}
                                    {outgoingInFlight.length > 0 && (
                                        <InboxSection
                                            title="You sent"
                                            count={outgoingInFlight.length}
                                            subtitle="Applications you've sent that hosts haven't resolved yet."
                                        >
                                            {outgoingInFlight.map(req => (
                                                <RequestCard
                                                    key={req.id}
                                                    request={req}
                                                    isIncoming={false}
                                                    onMessage={handleMessageRequest}
                                                    onNudge={handleNudgeApplication}
                                                    onReapply={handleReapply}
                                                />
                                            ))}
                                        </InboxSection>
                                    )}

                                    {inboxIsEmpty && (
                                        filterProperty ? (
                                            <div className={styles.emptyState}>
                                                <Icon name="filter" size="48" />
                                                <h3>No matches</h3>
                                                <p>No applications match your filter.</p>
                                                <button onClick={() => setFilterProperty('')} className={styles.ctaBtn}>
                                                    Clear Filter
                                                </button>
                                            </div>
                                        ) : (
                                            <div className={styles.emptyState}>
                                                <Icon name="inbox" size="48" />
                                                <h3>Your inbox is clear</h3>
                                                <p>New applications and replies will land here.</p>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}

                            {activeTab === 'waitlist' && (
                                <div className={styles.requestList}>
                                    {incomingWaitlisted.length > 0 ? (
                                        <>
                                            <p style={{ fontSize: '13.5px', color: '#6b7280', margin: '0 0 14px' }}>
                                                Applicants you've saved for later. They've been told you'll reach
                                                out when the opportunity opens up — oldest first.
                                            </p>
                                            {incomingWaitlisted.map(req => (
                                                <ApplicantCard
                                                    key={req.id}
                                                    request={req}
                                                    onOpenChat={handleOpenChatForApplication}
                                                    onStartCollab={handleAcceptRequest}
                                                    onWaitlist={null}
                                                    onDecline={handleDeclineRequest}
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Icon name="clock" size="48" />
                                            <h3>No one on your waitlist yet</h3>
                                            <p>
                                                When an application looks promising but the timing's wrong,
                                                add them here — they'll be told you'll reach out when you're ready.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'drafts' && (
                                <div className={styles.collabList}>
                                    {(data?.drafts?.length > 0) ? (
                                        data.drafts.map(draft => (
                                            <DraftCard
                                                key={draft.id}
                                                draft={draft}
                                                onClick={() => handleDraftClick(draft)}
                                                onDelete={handleDeleteDraft}
                                            />
                                        ))
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Icon name="file" size="48" />
                                            <h3>No drafts yet</h3>
                                            <p>Create a draft to build your collaboration proposal before sending.</p>
                                            <button
                                                onClick={() => setIsExploreModalOpen(true)}
                                                className={styles.ctaBtn}
                                            >
                                                Create Draft
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'completed' && (
                                <div className={styles.collabList}>
                                    {data?.grouped?.completed?.length > 0 ? (
                                        applyFilterSort(data.grouped.completed).length > 0 ? (
                                            applyFilterSort(data.grouped.completed).map(collab => (
                                                <CollabCard 
                                                    key={collab.id} 
                                                    collab={collab} 
                                                    onClick={() => handleCollabClick(collab)}
                                                    onRemove={setRemoveTarget}
                                                />
                                            ))
                                        ) : (
                                            <div className={styles.emptyState}>
                                                <Icon name="filter" size="48" />
                                                <h3>No matches</h3>
                                                <p>No completed collaborations match your filter.</p>
                                                <button onClick={() => setFilterProperty('')} className={styles.ctaBtn}>
                                                    Clear Filter
                                                </button>
                                            </div>
                                        )
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Icon name="check-circle" size="48" />
                                            <h3>No completed collaborations</h3>
                                            <p>Completed collaborations will appear here.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'declined' && (
                                <div className={styles.collabList}>
                                    {data?.grouped?.declined?.length > 0 ? (
                                        applyFilterSort(data.grouped.declined).length > 0 ? (
                                            applyFilterSort(data.grouped.declined).map(collab => (
                                                <CollabCard 
                                                    key={collab.id} 
                                                    collab={collab} 
                                                    onClick={() => handleCollabClick(collab)}
                                                    onRemove={setRemoveTarget}
                                                />
                                            ))
                                        ) : (
                                            <div className={styles.emptyState}>
                                                <Icon name="filter" size="48" />
                                                <h3>No matches</h3>
                                                <p>No declined collaborations match your filter.</p>
                                                <button onClick={() => setFilterProperty('')} className={styles.ctaBtn}>
                                                    Clear Filter
                                                </button>
                                            </div>
                                        )
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Icon name="x-circle" size="48" />
                                            <h3>No declined collaborations</h3>
                                            <p>Collaborations you decline will appear here.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'admin-all' && isAdmin && (
                                <div className={styles.collabList}>
                                    {adminLoading ? (
                                        <div className={styles.loadingState}>
                                            <div className={styles.spinner} />
                                            <p>Loading all collaborations...</p>
                                        </div>
                                    ) : adminError ? (
                                        <div className={styles.errorState}>
                                            <Icon name="alert-triangle" size="48" />
                                            <p>{adminError}</p>
                                            <button onClick={fetchAdminCollabs} className={styles.retryBtn}>
                                                Try Again
                                            </button>
                                        </div>
                                    ) : adminData?.collaborations?.length > 0 ? (
                                        applyFilterSort(adminData.collaborations).length > 0 ? (
                                            applyFilterSort(adminData.collaborations).map(collab => (
                                                <CollabCard
                                                    key={collab.id}
                                                    collab={collab}
                                                    onClick={() => handleCollabClick(collab)}
                                                    isAdmin
                                                    onAdminComplete={handleAdminComplete}
                                                    adminBusy={adminBusyId === collab.id}
                                                />
                                            ))
                                        ) : (
                                            <div className={styles.emptyState}>
                                                <Icon name="filter" size="48" />
                                                <h3>No matches</h3>
                                                <p>No collaborations match your filter.</p>
                                                <button onClick={() => setFilterProperty('')} className={styles.ctaBtn}>
                                                    Clear Filter
                                                </button>
                                            </div>
                                        )
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Icon name="briefcase" size="48" />
                                            <h3>No collaborations</h3>
                                            <p>There are no collaborations on the platform yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {deleteRequestTarget && (
                <RequestDeleteModal
                    request={deleteRequestTarget}
                    onConfirm={handleDeleteRequest}
                    onClose={() => setDeleteRequestTarget(null)}
                    deleting={deletingRequest}
                />
            )}

            {removeTarget && (
                <CollabRemoveModal
                    collab={removeTarget}
                    onDelete={handleDeleteCollab}
                    onDecline={handleDeclineCollab}
                    onClose={() => setRemoveTarget(null)}
                    deleting={deleting}
                    declining={declining}
                />
            )}

            <ExploreNewCollabModal
                visible={isExploreModalOpen}
                onClose={() => setIsExploreModalOpen(false)}
                onExplore={handleExploreCollab}
                onStartDraft={handleStartDraft}
            />

            <CollabPersonaModal
                isOpen={!!personaPickerRequest}
                onClose={() => setPersonaPickerRequest(null)}
                onSelect={(persona) => {
                    const request = personaPickerRequest;
                    setPersonaPickerRequest(null);
                    if (request) handleAcceptRequest(request, persona);
                }}
            />
        </div>
        )}
        </>
    );
};

export default Collabs;
